import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { EditorContent, useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import TextStyle from '@tiptap/extension-text-style';
import Color from '@tiptap/extension-color';
import FontFamily from '@tiptap/extension-font-family';
import Image from '@tiptap/extension-image';
import Table from '@tiptap/extension-table';
import TableRow from '@tiptap/extension-table-row';
import TableCell from '@tiptap/extension-table-cell';
import TableHeader from '@tiptap/extension-table-header';
import Link from '@tiptap/extension-link';
import { useDropzone } from 'react-dropzone';
import ReactSelect from 'react-select';
import { GithubPicker } from 'react-color';
import { convertBase64 } from './TiptapUtils';
import './Tiptap.css';
import FontSize from './FontSizeExtension';
import { BackgroundColor } from './BackgroundColorExtension';
import { FaBold, FaImage, FaItalic, FaLink, FaTable, FaUnderline } from 'react-icons/fa';
import { MdFormatColorFill, MdFormatColorText } from 'react-icons/md';
import { Tooltip, useTheme } from '@mui/material';

interface TiptapProps {
  content: string;
  editable?: boolean;
  minHeight?: string;
  maxHeight?: string;
  width?: string;
  minWidth?: string;
  maxWidth?: string;
}

export type TiptapRef = {
  getHTML: () => string;
};

// 최대 파일 크기 1MB
const MAX_FILE_SIZE = 1024 * 1024;

const extensions = [
  StarterKit,
  Underline,
  TextStyle,
  FontFamily,
  Color,
  FontSize,
  Image.configure({
    allowBase64: true // Base64 이미지를 렌더링 가능하게 설정
  }),
  Table.configure({ resizable: true }),
  TableRow,
  TableCell,
  TableHeader,
  Link,
  BackgroundColor.configure({ types: ['textStyle'] })
];

// 폰트 옵션
const fontOptions = [
  { value: 'inherit', label: '기본' },
  { value: 'Gungseo', label: '궁서체' }
];

// 폰트 크기 옵션
const fontSizeOptions = [
  { value: '12px', label: '12px' },
  { value: '14px', label: '14px' },
  { value: '16px', label: '16px' },
  { value: '18px', label: '18px' },
  { value: '24px', label: '24px' },
  { value: '32px', label: '32px' },
  { value: '48px', label: '48px' }
];

const Tiptap = forwardRef<TiptapRef, TiptapProps>(({ content, editable = true, minHeight, maxHeight, width, minWidth, maxWidth }, ref) => {
  const [error, setError] = useState<string | null>(null);
  const [fontColor, setFontColor] = useState<string>('#000000');
  const [backgroundColor, setBackgroundColor] = useState<string>('#ffffff');
  const [showColorPicker, setShowColorPicker] = useState<boolean>(false); // 글자 색상 피커 가시성 상태
  const [showBackgroundColorPicker, setShowBackgroundColorPicker] = useState<boolean>(false); // 배경 색상 피커 가시성 상태
  const colorPickerRef = useRef<HTMLDivElement | null>(null); // 색상 피커의 DOM 요소 참조
  const backgroundColorPickerRef = useRef<HTMLDivElement | null>(null); // 배경 색상 피커의 DOM 요소 참조
  // 다크 라이트 모드
  const theme = useTheme();

  const editor = useEditor({
    extensions,
    content,
    editable
  });

  useImperativeHandle(ref, () => ({
    getHTML: () => {
      return editor ? editor.getHTML() : '';
    }
  }));

  const onDrop = useCallback(
    async (acceptedFiles: File[]) => {
      const file = acceptedFiles[0];
      if (file.size > MAX_FILE_SIZE) {
        setError('이미지 파일 크기는 1MB 이하로 업로드해주세요.');
        return;
      }
      const base64 = await convertBase64(file);
      editor?.chain().focus().setImage({ src: base64 }).run();
      setError(null); // 업로드 성공 시 에러 메시지 초기화
    },
    [editor]
  );

  const { getRootProps, getInputProps } = useDropzone({
    onDrop,
    accept: {
      'image/*': []
    }
  });

  useEffect(() => {
    if (editor) {
      editor.commands.setContent(content);
    }
  }, [content, editor]);

  // 외부 클릭 감지 및 색상 피커 숨기기
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (colorPickerRef.current && !colorPickerRef.current.contains(event.target as Node)) {
        setShowColorPicker(false); // 글자 색상 피커 숨기기
      }
      if (backgroundColorPickerRef.current && !backgroundColorPickerRef.current.contains(event.target as Node)) {
        setShowBackgroundColorPicker(false); // 배경 색상 피커 숨기기
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [colorPickerRef, backgroundColorPickerRef]);

  if (!editor) {
    return null;
  }

  const customStyles = {
    control: (base: any) => ({
      ...base,
      minHeight: '30px',
      fontSize: '12px'
    }),
    valueContainer: (base: any) => ({
      ...base,
      padding: '0 6px'
    }),
    input: (base: any) => ({
      ...base,
      margin: '0'
    }),
    indicatorsContainer: (base: any) => ({
      ...base,
      height: '30px'
    })
  };

  const editorStyle: React.CSSProperties = {
    color: editable ? '#000000' : theme.palette?.tiptap?.readonlyText || '#000000',
    minHeight: minHeight ?? 'none',
    maxHeight: maxHeight ?? 'none',
    width: width ?? 'none',
    minWidth: minWidth ?? 'none',
    maxWidth: maxWidth ?? 'none',
    overflowY: maxHeight ? 'auto' : 'visible',
    cursor: editable ? 'text' : 'auto'
  };

  return (
    <div className="tiptap-editor">
      {editable && (
        <div
          className="tiptap-toolbar"
          style={{
            backgroundColor: theme.palette.tiptap.toolbar,
            color: theme.palette.tiptap.toolbarText,
            borderTopLeftRadius: '6px',
            borderTopRightRadius: '6px'
          }}
        >
          <ReactSelect
            options={fontOptions}
            onChange={(selectedOption) =>
              editor
                .chain()
                .focus()
                .setFontFamily(selectedOption?.value ?? 'inherit')
                .run()
            }
            defaultValue={fontOptions[0]}
            className="select-font"
            styles={customStyles}
          />
          <ReactSelect
            options={fontSizeOptions}
            onChange={(selectedOption) =>
              editor
                .chain()
                .focus()
                .setFontSize(selectedOption?.value ?? '16px')
                .run()
            }
            defaultValue={fontSizeOptions[2]} // 기본 폰트 크기는 16px로 설정
            className="select-fontsize"
            styles={customStyles}
          />
          <Tooltip title="굵게" placement="top">
            <button
              onClick={() => editor.chain().focus().toggleBold().run()}
              disabled={!editor.can().chain().focus().toggleBold().run()}
              className={editor.isActive('bold') ? 'is-active' : ''}
            >
              <FaBold />
            </button>
          </Tooltip>
          <Tooltip title="기울임" placement="top">
            <button
              onClick={() => editor.chain().focus().toggleItalic().run()}
              disabled={!editor.can().chain().focus().toggleItalic().run()}
              className={editor.isActive('italic') ? 'is-active' : ''}
            >
              <FaItalic />
            </button>
          </Tooltip>
          <Tooltip title="밑줄" placement="top">
            <button
              onClick={() => editor.chain().focus().toggleUnderline().run()}
              disabled={!editor.can().chain().focus().toggleUnderline().run()}
              className={editor.isActive('underline') ? 'is-active' : ''}
            >
              <FaUnderline />
            </button>
          </Tooltip>
          <Tooltip title="글자색 변경" placement="top">
            <div className="color-picker-button-wrapper">
              <button
                onClick={() => setShowColorPicker(!showColorPicker)} // 버튼 클릭 시 글자 색상 피커 보이기/숨기기
              >
                <MdFormatColorText />
              </button>
              {showColorPicker && (
                <div className="color-picker-popover" ref={colorPickerRef}>
                  <GithubPicker
                    color={fontColor}
                    colors={[
                      '#000000',
                      '#5F00FF',
                      '#0100FF',
                      '#1DDB16',
                      '#FFE400',
                      '#FFBB00',
                      '#FF0000',
                      '#BDBDBD',
                      '#D1B2FF',
                      '#B5B2FF',
                      '#B7F0B1',
                      '#FAED7D',
                      '#FFE08C',
                      '#FFA7A7',
                      '#FFFFFF',
                      '#E8D9FF',
                      '#DAD9FF',
                      '#CEFBC9',
                      '#FAF4C0',
                      '#FAECC5',
                      '#FFD8D8'
                    ]}
                    onChangeComplete={(color) => {
                      setFontColor(color.hex);
                      editor.chain().focus().setColor(color.hex).run();
                      // setShowColorPicker(false); // 색상을 선택하면 글자 색상 피커 숨기기
                    }}
                  />
                </div>
              )}
            </div>
          </Tooltip>
          <Tooltip title="배경색 변경" placement="top">
            <div className="background-color-picker-button-wrapper">
              <button
                onClick={() => setShowBackgroundColorPicker(!showBackgroundColorPicker)} // 버튼 클릭 시 배경 색상 피커 보이기/숨기기
              >
                <MdFormatColorFill />
              </button>
              {showBackgroundColorPicker && (
                <div className="color-picker-popover" ref={backgroundColorPickerRef}>
                  <GithubPicker
                    color={backgroundColor}
                    colors={[
                      '#000000',
                      '#5F00FF',
                      '#0100FF',
                      '#1DDB16',
                      '#FFE400',
                      '#FFBB00',
                      '#FF0000',
                      '#BDBDBD',
                      '#D1B2FF',
                      '#B5B2FF',
                      '#B7F0B1',
                      '#FAED7D',
                      '#FFE08C',
                      '#FFA7A7',
                      '#FFFFFF',
                      '#E8D9FF',
                      '#DAD9FF',
                      '#CEFBC9',
                      '#FAF4C0',
                      '#FAECC5',
                      '#FFD8D8'
                    ]}
                    onChangeComplete={(color) => {
                      setBackgroundColor(color.hex);
                      editor.chain().focus().setBackgroundColor(color.hex).run();
                      // setShowBackgroundColorPicker(false); // 색상을 선택하면 배경 색상 피커 숨기기
                    }}
                  />
                </div>
              )}
            </div>
          </Tooltip>

          <span className="toolbar-separator">|</span>

          <Tooltip title="표 삽입" placement="top">
            <button onClick={() => editor.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run()}>
              <FaTable />
            </button>
          </Tooltip>
          <button
            style={{ fontSize: '12px' }}
            onClick={() => editor.chain().focus().addColumnAfter().run()}
            disabled={!editor.can().chain().focus().addColumnAfter().run()}
          >
            열 추가
          </button>
          <button
            style={{ fontSize: '12px' }}
            onClick={() => editor.chain().focus().deleteColumn().run()}
            disabled={!editor.can().chain().focus().deleteColumn().run()}
          >
            열 삭제
          </button>
          <button
            style={{ fontSize: '12px' }}
            onClick={() => editor.chain().focus().addRowAfter().run()}
            disabled={!editor.can().chain().focus().addRowAfter().run()}
          >
            행 추가
          </button>
          <button
            style={{ fontSize: '12px' }}
            onClick={() => editor.chain().focus().deleteRow().run()}
            disabled={!editor.can().chain().focus().deleteRow().run()}
          >
            행 삭제
          </button>

          <span className="toolbar-separator">|</span>

          <Tooltip title="이미지 삽입" placement="top">
            <div style={{ margin: 0 }} {...getRootProps({ className: 'dropzone' })}>
              <input {...getInputProps()} />
              <button>
                <FaImage />
              </button>
            </div>
          </Tooltip>
          <Tooltip title="링크 삽입" placement="top">
            <button
              onClick={() => {
                const url = prompt('링크 URL을 입력하세요');
                if (url) {
                  editor.chain().focus().setLink({ href: url }).run();
                }
              }}
            >
              <FaLink />
            </button>
          </Tooltip>

          {error && <div className="error">{error}</div>}
        </div>
      )}
      <EditorContent editor={editor} style={editorStyle} onClick={() => editor?.commands.focus()} />
    </div>
  );
});

Tiptap.displayName = 'Tiptap';

export default Tiptap;
